// The rules engine: visibility, progress, outstanding items and prefill.
//
// ---------------------------------------------------------------------------
// Why this lives in a shared package
// ---------------------------------------------------------------------------
// In the previous version this logic existed THREE times — once on the server,
// once in the client app, once in the staff console — because all three need
// it. The web form has to show and hide questions as the client types; the API
// has to recompute the same thing so it never trusts the client's view of what
// was asked; and the staff console has to replay it to review an answer set.
//
// Three copies meant three chances to disagree, and they did: a question could
// be visible in the browser, counted as hidden by the exporter, and a client's
// receipt would silently vanish from the report. Now there is one
// implementation and everything imports it.
//
// Everything here is PURE. No database, no clock, no I/O. That is what makes it
// cheap to test exhaustively and safe to run on a phone.
import { VALUELESS_TYPES, } from './question.js';
// ---------------------------------------------------------------------------
// Emptiness
// ---------------------------------------------------------------------------
/**
 * Is this value "no answer"?
 *
 * Subtle but important: `false` and `0` are REAL answers. A client who enters 0
 * work kilometres has told us something, and a question answered "no" is
 * answered. Only null, undefined, whitespace and empty collections count as
 * blank. Getting this wrong would make progress bars lie.
 */
export function isBlank(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === 'boolean' || typeof value === 'number')
        return false;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value)) {
        if (value.length === 0)
            return true;
        // A repeater whose rows are all empty objects is blank in practice: the
        // client tapped "Add" and then typed nothing. Treating that as answered
        // would let an empty rental schedule read as complete.
        return value.every((row) => {
            if (typeof row === 'string')
                return row.trim() === '';
            return Object.values(row).every((v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === ''));
        });
    }
    return false;
}
export function isAnswered(value) {
    return !isBlank(value);
}
// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------
/**
 * Compare an actual answer against a rule's expected value.
 *
 * A multiselect answer is an array, and "is this option chosen?" reads
 * naturally as equality to whoever authored the template — so an array actual
 * means "does it contain this?". Comparison is done on strings because answers
 * arrive from JSON, HTML inputs and a React Native form, and `'2'` from one and
 * `2` from another must behave identically.
 */
function valueMatches(actual, expected) {
    if (Array.isArray(actual)) {
        return actual.some((item) => {
            const scalar = typeof item === 'object' && item !== null ? undefined : item;
            return String(scalar ?? '') === String(expected ?? '');
        });
    }
    return String(actual ?? '') === String(expected ?? '');
}
/** Numeric comparison that refuses to guess. Non-numeric answers never match. */
function numericCompare(actual, expected, compare) {
    const a = typeof actual === 'number' ? actual : Number(actual);
    const b = typeof expected === 'number' ? expected : Number(expected);
    if (!Number.isFinite(a) || !Number.isFinite(b))
        return false;
    return compare(a, b);
}
/**
 * Evaluate a visibility rule against a set of answers.
 *
 * The operator switch is exhaustive — the `never` assignment at the end is a
 * compile error if anyone adds an operator to the schema without handling it
 * here. That is deliberate: the previous version silently returned `true` for
 * operators it did not recognise.
 */
export function isVisible(rule, answers) {
    if (!rule)
        return true;
    if ('all' in rule)
        return rule.all.every((r) => isVisible(r, answers));
    if ('any' in rule)
        return rule.any.some((r) => isVisible(r, answers));
    if ('not' in rule)
        return !isVisible(rule.not, answers);
    const actual = answers[rule.code];
    switch (rule.op) {
        case 'eq':
            return valueMatches(actual, rule.value);
        case 'ne':
            return !valueMatches(actual, rule.value);
        case 'in':
            return Array.isArray(rule.value) && rule.value.some((v) => valueMatches(actual, v));
        case 'gt':
            return numericCompare(actual, rule.value, (a, b) => a > b);
        case 'lt':
            return numericCompare(actual, rule.value, (a, b) => a < b);
        case 'filled':
            return isAnswered(actual);
        case 'empty':
            return isBlank(actual);
        default: {
            const exhaustive = rule.op;
            throw new Error(`Unhandled comparison operator: ${String(exhaustive)}`);
        }
    }
}
/** Is this question shown, taking its section's own rule into account? */
export function isQuestionVisible(template, code, answers) {
    for (const section of template.sections) {
        const question = section.questions.find((q) => q.code === code);
        if (!question)
            continue;
        return isVisible(section.visibleIf, answers) && isVisible(question.visibleIf, answers);
    }
    return false;
}
/** The sections a client can currently see, in order. */
export function visibleSections(template, answers) {
    return template.sections.filter((s) => isVisible(s.visibleIf, answers));
}
/** The questions a client can currently see within one section, in order. */
export function visibleQuestions(section, answers) {
    return section.questions.filter((q) => isVisible(q.visibleIf, answers));
}
/**
 * How far through is this client?
 *
 * Counts only questions that are actually VISIBLE given their current answers.
 * Counting hidden ones would strand someone with no rental property at 60%
 * forever, with no way to find the missing questions because they are hidden.
 *
 * Headings and upload-only questions are excluded: they hold no value, so
 * including them would make 100% unreachable.
 */
export function computeProgress(template, answers) {
    const sections = [];
    let total = 0;
    let answered = 0;
    for (const section of visibleSections(template, answers)) {
        let sectionTotal = 0;
        let sectionAnswered = 0;
        for (const question of visibleQuestions(section, answers)) {
            if (VALUELESS_TYPES.has(question.type))
                continue;
            sectionTotal += 1;
            if (isAnswered(answers[question.code]))
                sectionAnswered += 1;
        }
        total += sectionTotal;
        answered += sectionAnswered;
        sections.push({
            code: section.code,
            title: section.title,
            total: sectionTotal,
            answered: sectionAnswered,
            // A section with nothing to answer (all hidden, or headings only) counts
            // as complete — otherwise its chip would never get a tick and the client
            // would keep returning to it looking for something to do.
            complete: sectionAnswered === sectionTotal,
        });
    }
    return {
        total,
        answered,
        percent: total === 0 ? 100 : Math.round((answered / total) * 100),
        sections,
    };
}
/**
 * Visible, required questions that are still blank.
 *
 * Shown on the review screen so a client knows exactly what is missing — and
 * each one is tappable, jumping straight to its section. They can still submit
 * without answering them: a partly-filled questionnaire is far more useful to
 * the preparer than an abandoned one.
 */
export function outstandingRequired(template, answers) {
    const out = [];
    for (const section of visibleSections(template, answers)) {
        for (const question of visibleQuestions(section, answers)) {
            if (!question.required)
                continue;
            if (VALUELESS_TYPES.has(question.type))
                continue;
            if (isAnswered(answers[question.code]))
                continue;
            out.push({
                code: question.code,
                label: question.label,
                sectionCode: section.code,
                sectionTitle: section.title,
            });
        }
    }
    return out;
}
/**
 * The practice's chase list, and the fix for a bug that cost us real evidence.
 *
 * Two distinct problems:
 *
 * 1. 'said-yes-no-evidence' — the client ticked yes to a deduction that needs
 *    substantiation and attached nothing. This is the single most useful thing
 *    a preparer can be told before they start.
 *
 * 2. 'orphaned' — files attached to a question that a LATER answer has since
 *    hidden. A client photographs fuel receipts against D1_COST_FUEL, then
 *    picks the logbook method, which hides every actual-cost question. The
 *    files are still stored, but without this they appear nowhere: not in the
 *    export, not in the staff review. Nobody would know to look for them.
 *
 * `attachmentCounts` maps question code to how many files hang off it.
 */
export function findEvidenceGaps(template, answers, attachmentCounts) {
    const gaps = [];
    const visibleCodes = new Set();
    for (const section of visibleSections(template, answers)) {
        for (const question of visibleQuestions(section, answers)) {
            visibleCodes.add(question.code);
            const hasFiles = (attachmentCounts[question.code] ?? 0) > 0;
            if (question.allowFiles && !hasFiles && answers[question.code] === 'yes') {
                gaps.push({
                    code: question.code,
                    label: question.label,
                    sectionTitle: section.title,
                    reason: 'said-yes-no-evidence',
                });
            }
        }
    }
    // Anything with files whose question is no longer being asked.
    for (const [code, count] of Object.entries(attachmentCounts)) {
        if (count <= 0 || visibleCodes.has(code))
            continue;
        for (const section of template.sections) {
            const question = section.questions.find((q) => q.code === code);
            if (!question)
                continue;
            gaps.push({
                code,
                label: question.label,
                sectionTitle: section.title,
                reason: 'orphaned',
            });
            break;
        }
    }
    return gaps;
}
/**
 * Work out what to carry forward from last year into a new questionnaire.
 *
 * This is the whole reason question codes are stable: matching is a plain code
 * lookup, with no mapping table and no migration when the template changes.
 * Add, remove or reorder questions for FY2027 and anything that kept its code
 * still inherits its answer.
 *
 * Carried values are flagged as prefill by the caller so the UI can show them
 * as "Last year you said..." rather than pretending the client has answered.
 * That distinction matters: an unreviewed guess presented as an answer is how
 * last year's figures end up silently in this year's return.
 */
export function computePrefill(newTemplate, priorAnswers) {
    const values = {};
    const skipped = {};
    const byCode = new Map();
    for (const section of newTemplate.sections) {
        for (const question of section.questions)
            byCode.set(question.code, question);
    }
    for (const [code, value] of Object.entries(priorAnswers)) {
        const question = byCode.get(code);
        if (!question) {
            skipped[code] = 'not-in-template';
            continue;
        }
        if (!question.prefill) {
            skipped[code] = 'prefill-disabled';
            continue;
        }
        if (value === undefined || isBlank(value)) {
            skipped[code] = 'blank-last-year';
            continue;
        }
        values[code] = value;
    }
    return { values, skipped };
}
// ---------------------------------------------------------------------------
// Financial years
// ---------------------------------------------------------------------------
/**
 * The financial year currently being LODGED — the one that has just ended, not
 * the one in progress.
 *
 * An Australian FY runs 1 July to 30 June and is named for the year it ends in,
 * so FY2026 is 1 Jul 2025 to 30 Jun 2026. Both halves of the cycle:
 *
 *   Jul-Dec 2026  ->  FY2026 has just closed, clients lodge it  ->  2026
 *   Jan-Jun 2027  ->  still lodging FY2026 (agent due 15 May)   ->  2026
 *
 * This had an off-by-one-year bug in the previous version that invited every
 * client into the wrong year for six months of the cycle, so it is tested
 * across both boundaries.
 */
export function taxYearBeingLodged(now = new Date()) {
    const year = now.getFullYear();
    // getMonth() is 0-based, so 6 is July.
    return now.getMonth() >= 6 ? year : year - 1;
}
/** Human label for a financial year, e.g. "FY2026 (1 Jul 2025 – 30 Jun 2026)". */
export function financialYearLabel(year) {
    return `FY${year} (1 Jul ${year - 1} – 30 Jun ${year})`;
}
//# sourceMappingURL=rules.js.map